import {
    FC,
    useCallback,
    useEffect,
    useRef,
    useState,
    type ChangeEvent,
    type DragEvent,
    type MutableRefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { filterFilesByAccept, normalizeDropzoneAccept } from '../../utils/collectFilesFromDropEvent';

export type StudioFileUploadDropzoneProps = {
    onFilesSelected: (files: File[]) => void | Promise<void>;
    disabled?: boolean;
    /** MIME listesi; örn. image/jpeg,image/png veya tüm dosya tipleri */
    accept?: string;
    title?: string;
    onBrowseOpen?: () => void;
    onBrowseCancel?: () => void;
    /** Native file input ref — picker cancel/focus fallback için */
    inputRef?: MutableRefObject<HTMLInputElement | null>;
};

export const StudioFileUploadDropzone: FC<StudioFileUploadDropzoneProps> = ({
    onFilesSelected,
    disabled = false,
    accept,
    title,
    onBrowseOpen,
    onBrowseCancel,
    inputRef,
}) => {
    const { t } = useTranslation();
    const localInputRef = useRef<HTMLInputElement | null>(null);
    const [isDragActive, setIsDragActive] = useState(false);

    useEffect(() => {
        if (!inputRef) {
            return;
        }
        inputRef.current = localInputRef.current;
    });

    const processFiles = useCallback(
        async (files: File[]) => {
            const filtered = filterFilesByAccept(files, accept);
            if (filtered.length === 0) {
                if (files.length > 0) {
                    await Swal.fire(t('studio.bulkImageUpload.invalidFileType'), '', 'warning');
                }
                return;
            }
            await onFilesSelected(filtered);
        },
        [accept, onFilesSelected, t],
    );

    const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!disabled) {
            setIsDragActive(true);
        }
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
            return;
        }
        setIsDragActive(false);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
        }
        if (!disabled) {
            setIsDragActive(true);
        }
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
        if (disabled) {
            return;
        }
        const files = event.dataTransfer?.files?.length ? Array.from(event.dataTransfer.files) : [];
        void processFiles(files);
    };

    const handleBrowseClick = () => {
        if (disabled) {
            return;
        }
        onBrowseOpen?.();
        localInputRef.current?.click();
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files?.length ? Array.from(event.target.files) : [];
        event.target.value = '';
        if (files.length === 0) {
            onBrowseCancel?.();
            return;
        }
        void processFiles(files);
    };

    const hint = isDragActive ? t('general.dragToUpload') : t('studio.upload.dragDropInfo');
    const inputAccept = normalizeDropzoneAccept(accept);

    return (
        <Box
            component="div"
            data-studio-dropzone="true"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleBrowseClick}
            sx={(theme) => ({
                mb: 3,
                p: 4,
                border: `2px ${isDragActive ? 'dashed' : 'solid'} ${
                    isDragActive ? theme.palette.primary.main : theme.palette.divider
                }`,
                borderRadius: 3,
                backgroundColor: isDragActive
                    ? alpha(theme.palette.primary.main, 0.08)
                    : theme.palette.grey[50],
                opacity: disabled ? 0.45 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                cursor: disabled ? 'not-allowed' : 'pointer',
                textAlign: 'center',
                outline: 'none',
                userSelect: 'none',
            })}
        >
            <input
                ref={localInputRef}
                type="file"
                multiple
                accept={inputAccept}
                disabled={disabled}
                style={{ display: 'none' }}
                onClick={(event) => event.stopPropagation()}
                onChange={handleInputChange}
            />

            <Box
                sx={(theme) => ({
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 72,
                    height: 72,
                    mb: 1.5,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.primary.main, isDragActive ? 0.16 : 0.1),
                    pointerEvents: 'none',
                })}
            >
                <CloudUploadIcon color="primary" sx={{ fontSize: 36 }} />
            </Box>

            <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary', pointerEvents: 'none' }}
            >
                {title ?? t('studio.upload.selectFiles')}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ pointerEvents: 'none' }}>
                {hint}
            </Typography>
        </Box>
    );
};
